import { actionsPinnedRight } from '../../utils/entity-list';
import { useEntityNotes } from '../../components/entity-notes/useEntityNotes';
import { inNotesCell, notesGridSx } from '../../components/entity-notes/notesColumn';
import { useState, useRef } from 'react';
import {
  Box, Button, Typography, Chip, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, CircularProgress, Divider, TextField,
} from '@mui/material';
import { DataGridPremium, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import type { GridSortModel } from '@mui/x-data-grid-premium';
import { Add, Edit, Delete, ContentCopy } from '@mui/icons-material';
import TooltipIconButton from '../../components/TooltipIconButton';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ActivityCell, { activityOrderBy } from '../../components/ActivityCell';
import RefreshActivityButton from '../../components/RefreshActivityButton';
import { useSnackbar } from 'notistack';
import { flowsService, FlowRuleGroup, MergeCluster } from '../../services/flows.service';
import { useModalState } from '../../hooks/useModalState';
import { useServerPagination } from '../../hooks/useServerPagination';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { useDebounced } from '../../hooks/useDebounced';
import EntityFilterSelect from '../../components/EntityFilterSelect';
import { EntityOption } from '../../components/entity-fetchers';

/** One-line summary of what a group routes to, so the table is readable at a glance. */
function describeGroup(g: FlowRuleGroup): string {
  if (!g.paths?.length) return 'No paths';
  return g.paths
    .map((p) => {
      const dest = p.lp_name ? `LP: ${p.lp_name}` : 'Direct';
      const offers = (p.offers || []).length;
      return `${dest} → ${offers} offer${offers === 1 ? '' : 's'} (w:${p.weight})`;
    })
    .join('  |  ');
}

export default function RuleGroupsPage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [mergeOpen, setMergeOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [usageGroup, setUsageGroup] = useState<FlowRuleGroup | null>(null);
  const { setModal } = useModalState('rulegroup');

  const [search, setSearch] = useUrlState('q', '', codecs.str);
  const [flowSel, setFlowSel] = useUrlState<EntityOption[]>('flow', [], codecs.json<EntityOption[]>());
  const [tsSel, setTsSel] = useUrlState<EntityOption[]>('ts', [], codecs.json<EntityOption[]>());
  const [netSel, setNetSel] = useUrlState<EntityOption[]>('net', [], codecs.json<EntityOption[]>());
  const debouncedSearch = useDebounced(search, 250);
  const flowIds = flowSel.map((s) => s.id);
  const trafficSourceIds = tsSel.map((s) => s.id);
  const networkIds = netSel.map((s) => s.id);
  const filterKey = `${debouncedSearch}|${flowIds.join(',')}|${trafficSourceIds.join(',')}|${networkIds.join(',')}`;
  const { gridProps, offset, limit } = useServerPagination(50, filterKey);
  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);
  const [sort, setSort] = useState<GridSortModel>([{ field: '_activity', sort: 'desc' }]);

  const orderBy = activityOrderBy(sort);

  const { data: result, isFetching } = useQuery({
    queryKey: ['rule-groups-library', filterKey, offset, limit, sort],
    queryFn: () => flowsService.listRuleGroups({
      search: debouncedSearch,
      flow_ids: flowIds,
      traffic_source_ids: trafficSourceIds,
      network_ids: networkIds,
      limit,
      offset,
      ...(orderBy && { order_by: orderBy, order: sort[0]?.sort === 'asc' ? 'asc' as const : 'desc' as const }),
    }),
    placeholderData: (prev) => prev,
  });
  const groups = result?.data;
  const total = result?.total ?? 0;

  // Only fetched when the merge dialog opens — it's a full structural scan.
  const { data: preview, isLoading: previewLoading } = useQuery({
    queryKey: ['rule-groups-merge-preview'],
    queryFn: () => flowsService.previewMerge(),
    enabled: mergeOpen,
  });

  const { data: usingFlows } = useQuery({
    queryKey: ['rule-group-flows', usageGroup?.id],
    queryFn: () => flowsService.flowsUsingGroup(usageGroup!.id),
    enabled: !!usageGroup,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['rule-groups-library'] });
    queryClient.invalidateQueries({ queryKey: ['rule-groups-merge-preview'] });
    queryClient.invalidateQueries({ queryKey: ['flows'] });
  };
  const notes = useEntityNotes('rule-group');


  /** Apply every cluster in the preview. Each is an explicit keep/merge id list. */
  const applyAll = async (clusters: MergeCluster[]) => {
    setApplying(true);
    try {
      let merged = 0;
      for (const c of clusters) {
        const res = await flowsService.applyMerge(c.keep.id, c.duplicates.map((d) => d.id));
        merged += res.merged;
      }
      refresh();
      setMergeOpen(false);
      enqueueSnackbar(`Merged ${merged} duplicate group${merged === 1 ? '' : 's'}`, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Merge failed', { variant: 'error' });
    } finally {
      setApplying(false);
    }
  };

  const columns: GridColDef[] = [
    { field: 'ref', headerName: 'Ref', width: 90, sortable: false, valueGetter: (_v, row) => row.ref || '—' },
    { field: 'name', headerName: 'Rule Group', flex: 1, minWidth: 180,
      valueGetter: (v) => v || 'Unnamed group' },
    { field: 'routes_to', headerName: 'Routes to', flex: 2, minWidth: 260, sortable: false,
      valueGetter: (_v, row) => describeGroup(row) },
    { field: 'conditions', headerName: 'Conditions', width: 110, sortable: false,
      valueGetter: (_v, row) => (row.conditions?.length ? String(row.conditions.length) : '—') },
    {
      // 180, not 150: this column carries three figures (expected | 24h | 1h) and a busy
      // group runs to "21.5K | 18.4K | 1.2K", which ellipsizes at the narrower width.
      field: '_activity', headerName: 'Activity', width: 180, filterable: false,
      description: 'Non-bot visits in the last hour and the last 24 hours, across every flow using this group. Sorts by the last 24 hours.',
      renderCell: (p: GridRenderCellParams) => (
        <ActivityCell
          activity={p.row.activity_status} senderLabel="Flows sending to it"
          parts={['expected', 'h24', 'h1']} kind="rule_group" entityId={p.row.id}
          updatedAt={p.row.activity_updated_at}
        />
      ),
    },
    notes.column,
    {
      // Counted in JS from flow_group_assignments, so it never reaches SQL — offering the
      // sort would silently fall through to ordering by name.
      field: 'used_by_flow_count', headerName: 'Used by', width: 130, sortable: false,
      renderCell: (p: GridRenderCellParams) => {
        const n = Number(p.value ?? 0);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip
              size="small"
              // Shared groups are the ones where an edit reaches other flows —
              // colour them so that's obvious while scanning.
              color={n > 1 ? 'warning' : n === 0 ? 'default' : 'success'}
              variant={n > 1 ? 'filled' : 'outlined'}
              label={`${n} flow${n === 1 ? '' : 's'}`}
              onClick={() => setUsageGroup(p.row)}
            />
          </Box>
        );
      },
    },
    {
      field: 'actions', headerName: '', width: 124, sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <TooltipIconButton size="small" title="Edit" onClick={() => setModal({ open: true, id: p.row.id, reason: 'Update' })}>
            <Edit sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          {/* An independent copy — the way to make one flow diverge from a group
              the others share, without rebuilding its routing by hand. */}
          <TooltipIconButton size="small" title="Duplicate" onClick={() => duplicate(p.row)}>
            <ContentCopy sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          {/* Only an unused group can be deleted; the DB refuses the rest anyway. */}
          <TooltipIconButton
            size="small" color="error"
            disabled={(p.row.used_by_flow_count ?? 0) > 0}
            title={(p.row.used_by_flow_count ?? 0) > 0 ? 'Detach from its flows first' : 'Delete'}
            onClick={() => remove(p.row)}
          >
            <Delete sx={{ fontSize: 16 }} />
          </TooltipIconButton>
        </Box>
      ),
    },
  ];

  /** Clone server-side (paths and offers included), then open the copy for editing. */
  const duplicate = async (g: FlowRuleGroup) => {
    try {
      const copy = await flowsService.cloneRuleGroup(g.id, `${g.name || 'Unnamed group'} (copy)`);
      refresh();
      enqueueSnackbar('Duplicated — the copy is attached to no flows yet', { variant: 'success' });
      setModal({ open: true, id: copy.id, reason: 'Update' });
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      enqueueSnackbar(err?.response?.data?.error?.message || 'Duplicate failed', { variant: 'error' });
    }
  };


  const remove = async (g: FlowRuleGroup) => {
    try {
      await flowsService.deleteRuleGroup(g.id);
      refresh();
      enqueueSnackbar('Rule group deleted', { variant: 'success' });
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      enqueueSnackbar(err?.response?.data?.error?.message || 'Delete failed', { variant: 'error' });
    }
  };

  const shared = (groups || []).filter((g) => (g.used_by_flow_count ?? 0) > 1).length;
  const unused = (groups || []).filter((g) => (g.used_by_flow_count ?? 0) === 0).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>Rule Groups</Typography>
          <Typography variant="body2" color="text.secondary">
            Routing shared across flows — edit once, applies everywhere it's used.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <RefreshActivityButton invalidateKeys={['rule-groups-library', 'flows']} />
          <Button variant="outlined" onClick={() => setMergeOpen(true)}>Find duplicates</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setModal({ open: true, reason: 'Create' })}>
            New Rule Group
          </Button>
        </Box>
      </Box>

      <Box ref={filterRef} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small" placeholder="Search rule groups…" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ width: 280 }}
        />
        <EntityFilterSelect field="flow_id" value={flowSel} onChange={setFlowSel} label="Flow" sx={{ minWidth: 170, maxWidth: 280 }} />
        <EntityFilterSelect field="traffic_source_id" value={tsSel} onChange={setTsSel} label="Traffic Source" sx={{ minWidth: 180, maxWidth: 280 }} />
        <EntityFilterSelect field="network_id" value={netSel} onChange={setNetSel} label="Network" sx={{ minWidth: 160, maxWidth: 280 }} />
        <Chip label={`${total} group${total === 1 ? '' : 's'}`} />
        {/* Counted within the current page, not the whole library. */}
        <Chip color={shared ? 'warning' : 'default'} label={`${shared} shared on this page`} />
        {unused > 0 && <Chip variant="outlined" label={`${unused} unused`} />}
      </Box>

      <DataGridPremium
        rows={groups || []}
        columns={columns}
        loading={isFetching}
        {...gridProps(result?.total)}
        sortingMode="server"
        sortModel={sort}
        onSortModelChange={setSort}
        disableRowSelectionOnClick
        onRowDoubleClick={(params: GridRowParams, event: React.MouseEvent) => {
          const target = event.target as HTMLElement;
          if (target.closest('[data-field="actions"]')) return;
          if (notes.editable && inNotesCell(target)) return;
          setModal({ open: true, id: params.row.id, reason: 'Update' });
        }}
        {...notes.gridProps}
        initialState={actionsPinnedRight()}
        sx={{ ...notesGridSx, bgcolor: 'background.paper', borderRadius: 2, height: gridHeight }}
      />

      {/* Which flows use a group — the concrete answer behind "used by N flows". */}
      <Dialog open={!!usageGroup} onClose={() => setUsageGroup(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{usageGroup?.name || 'Unnamed group'} — used by</DialogTitle>
        <DialogContent dividers>
          {(usingFlows || []).length === 0
            ? <Typography variant="body2" color="text.secondary">No flows use this group.</Typography>
            : (usingFlows || []).map((f) => (
                <Typography key={f.id} variant="body2" sx={{ py: 0.5 }}>{f.name}</Typography>
              ))}
        </DialogContent>
        <DialogActions><Button onClick={() => setUsageGroup(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Merge preview — always a dry run first; nothing changes until confirmed. */}
      <Dialog open={mergeOpen} onClose={() => setMergeOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Merge duplicate rule groups</DialogTitle>
        <DialogContent dividers>
          {previewLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
          {!previewLoading && (preview?.clusters.length ?? 0) === 0 && (
            <Typography variant="body2" color="text.secondary">
              No duplicate groups found — every group routes differently.
            </Typography>
          )}
          {!previewLoading && (preview?.clusters.length ?? 0) > 0 && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This links flows together. Afterwards, editing one of these groups changes
                every flow using it — that's the point, but it can't be undone by a single
                click. Routing itself does not change: only identical groups are merged.
              </Alert>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <b>{preview!.groups_removed}</b> duplicate group{preview!.groups_removed === 1 ? '' : 's'} would fold
                into <b>{preview!.clusters.length}</b> shared group{preview!.clusters.length === 1 ? '' : 's'},
                affecting <b>{preview!.flows_affected}</b> flow{preview!.flows_affected === 1 ? '' : 's'}.
              </Typography>
              {preview!.clusters.map((c) => (
                <Paper key={c.keep.id} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Keep: {c.keep.name || 'Unnamed group'}{' '}
                    <Chip size="small" label={`${c.keep.used_by_flow_count ?? 0} flows`} sx={{ ml: 0.5 }} />
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{describeGroup(c.keep)}</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Folding in {c.duplicates.length} identical group{c.duplicates.length === 1 ? '' : 's'}:
                  </Typography>
                  {c.duplicates.map((d) => (
                    <Typography key={d.id} variant="body2" sx={{ pl: 1 }}>
                      • {d.name || 'Unnamed group'}{' '}
                      <Chip size="small" variant="outlined" label={`${d.used_by_flow_count ?? 0} flows`} />
                    </Typography>
                  ))}
                </Paper>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={applying || (preview?.clusters.length ?? 0) === 0}
            startIcon={applying ? <CircularProgress size={16} /> : undefined}
            onClick={() => applyAll(preview!.clusters)}
          >
            {applying ? 'Merging…' : 'Merge all'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
